/**
 * Geo-aware default currency picker for pricing UIs.
 *
 * Resolution order (first match wins):
 *   1. localStorage `preferred_currency` (set every time the user toggles)
 *   2. Browser timezone → country code → currency
 *   3. Browser locale region (e.g. en-GB, fr-FR) → currency
 *   4. Fallback: 'usd'
 *
 * UK → 'gbp', EU → 'eur', everything else → 'usd'.
 * The user's manual choice always takes priority over geo detection.
 */

const STORAGE_KEY = 'preferred_currency';

const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'
]);

// Selective timezone → ISO country mapping (covers majority of UK + EU + common rest-of-world).
const TZ_TO_COUNTRY = {
  'Europe/London': 'GB', 'Europe/Belfast': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Warsaw': 'PL',
  'Europe/Lisbon': 'PT',
  'Europe/Athens': 'GR',
  'Europe/Helsinki': 'FI',
  'Europe/Stockholm': 'SE',
  'Europe/Copenhagen': 'DK',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Bucharest': 'RO',
  'Europe/Sofia': 'BG',
  'Europe/Zagreb': 'HR',
  'Europe/Bratislava': 'SK',
  'Europe/Ljubljana': 'SI',
  'Europe/Luxembourg': 'LU',
  'Europe/Vilnius': 'LT',
  'Europe/Riga': 'LV',
  'Europe/Tallinn': 'EE',
  'Europe/Malta': 'MT',
  'Asia/Nicosia': 'CY',
};

function currencyForCountry(cc) {
  if (!cc) return null;
  const code = String(cc).toUpperCase();
  if (code === 'GB' || code === 'UK') return 'gbp';
  if (EU_COUNTRIES.has(code)) return 'eur';
  return 'usd';
}

function fromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TZ_TO_COUNTRY[tz]) return currencyForCountry(TZ_TO_COUNTRY[tz]);
    if (tz && tz.startsWith('Europe/')) return 'eur'; // best-guess for unmapped European tz
  } catch (_e) { /* ignore */ }
  return null;
}

function fromLocale() {
  try {
    const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
    // en-gb, en-uk
    if (lang === 'en-gb' || lang === 'en-uk') return 'gbp';
    const m = lang.match(/-([a-z]{2})$/);
    if (m) return currencyForCountry(m[1]);
  } catch (_e) { /* ignore */ }
  return null;
}

export function getStoredCurrency() {
  try {
    const v = (localStorage.getItem(STORAGE_KEY) || '').toLowerCase();
    if (v === 'gbp' || v === 'eur' || v === 'usd') return v;
  } catch (_e) { /* ignore */ }
  return null;
}

export function setStoredCurrency(code) {
  try {
    if (!code) return;
    localStorage.setItem(STORAGE_KEY, String(code).toLowerCase());
  } catch (_e) { /* ignore */ }
}

export function detectDefaultCurrency() {
  return getStoredCurrency() || fromTimezone() || fromLocale() || 'usd';
}
